import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, GripVertical, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { METRICS } from "@/lib/metrics";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/common";

const ModePick = ({ mode, onChange }) => (
  <div className="inline-flex rounded-full border p-1" data-testid="layout-mode">
    <Button type="button" size="sm" variant={mode === "kartu" ? "default" : "ghost"} className="h-9 rounded-full" onClick={() => onChange("kartu")} data-testid="layout-mode-kartu"><LayoutGrid className="h-4 w-4 mr-1" />Kartu</Button>
    <Button type="button" size="sm" variant={mode === "tabel" ? "default" : "ghost"} className="h-9 rounded-full" onClick={() => onChange("tabel")} data-testid="layout-mode-tabel"><List className="h-4 w-4 mr-1" />Tabel</Button>
  </div>
);

const ItemRow = ({ it, i, last, drag, onDrag, onDrop, onMove, onToggle }) => (
  <div draggable onDragStart={() => onDrag(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
    className={`flex items-center gap-3 px-4 py-3 bg-card ${drag === i ? "opacity-50" : ""}`} data-testid={`layout-item-${it.key}`}>
    <GripVertical className="h-5 w-5 text-muted-foreground cursor-grab shrink-0" />
    <span className={`flex-1 text-sm font-medium ${it.visible ? "" : "text-muted-foreground line-through"}`}>{METRICS[it.key].label}</span>
    <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={i === 0} onClick={() => onMove(i, -1)} aria-label="Naik" data-testid={`layout-up-${it.key}`}><ArrowUp className="h-4 w-4" /></Button>
    <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={last} onClick={() => onMove(i, 1)} aria-label="Turun" data-testid={`layout-down-${it.key}`}><ArrowDown className="h-4 w-4" /></Button>
    <Switch checked={it.visible} onCheckedChange={() => onToggle(i)} data-testid={`layout-visible-${it.key}`} />
  </div>
);

export default function AturLaporanPage({ title = "Atur Dashboard" }) {
  const [l, setL] = useState(null);
  const [drag, setDrag] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/metrics/layout").then((r) => setL(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const setItems = (items) => setL((x) => ({ ...x, items }));
  const move = (i, d) => { const n = [...l.items]; [n[i], n[i + d]] = [n[i + d], n[i]]; setItems(n); };
  const drop = (to) => { if (drag === null || drag === to) { setDrag(null); return; } const n = [...l.items]; const [it] = n.splice(drag, 1); n.splice(to, 0, it); setItems(n); setDrag(null); };
  const toggle = (i) => setItems(l.items.map((x, j) => (j === i ? { ...x, visible: !x.visible } : x)));
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/metrics/layout", l); setL(data); toast.success("Tampilan disimpan"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!l) return <p className="text-muted-foreground">Memuat...</p>;
  return (
    <div className="max-w-2xl">
      <PageHeader title={title} action={<Button onClick={save} disabled={busy} className="rounded-full h-11 px-6" data-testid="layout-save">{busy ? "Menyimpan..." : "Simpan"}</Button>} />
      <div className="panel space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3"><div><p className="font-semibold">Bentuk tampilan</p><p className="text-xs text-muted-foreground">Pilih kartu atau tabel</p></div><ModePick mode={l.mode} onChange={(mode) => setL({ ...l, mode })} /></div>
        <div>
          <p className="font-semibold">Urutan & visibilitas</p>
          <p className="text-xs text-muted-foreground mb-3">Seret untuk mengubah urutan (atau pakai panah). Matikan untuk menyembunyikan.</p>
          <div className="rounded-2xl border divide-y overflow-hidden" data-testid="layout-list">
            {l.items.map((it, i) => <ItemRow key={it.key} it={it} i={i} last={i === l.items.length - 1} drag={drag} onDrag={setDrag} onDrop={drop} onMove={move} onToggle={toggle} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

export const AturLaporanViewer = () => <AturLaporanPage title="Atur Laporan" />;
